import React, { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { isLoggedIn } from "../../../helpers/auth";
import { I18N_NAMESPACES } from "../../../helpers/constants";
import { useDeviceSize } from "../../../helpers/hooks";
import { colors } from "../../../styles/variables";
import { getCashAmountLabel } from "../../../helpers/i18n";
import {
  MerchCardImg,
  MerchCardWrapper,
  MerchCardImageHolder,
  MerchCardFooter,
  MerchTitle,
  MerchCardFooterBottomRow,
  InfoWrapper,
  InfoLabel,
  PriceWrapper,
  PriceLabel,
  AddButton,
  Tag,
  CheckIcon,
  TagText,
  RewardLockedIcon,
  AddButtonVariant,
} from "./style";

export interface MerchCardProps {
  name: string;
  linkUrl: string;
  pointsCost: number | null;
  cashCost: number | null;
  image: string;
  isNew: boolean;
  isLowStock: boolean;
  hasStock: boolean;
  isCollected: boolean;
  unlockLevel?: number | null;
  redeemWithPoints: boolean;
  onAddButtonClick: (event: React.MouseEvent<HTMLElement>) => void;
  onMerchCardClick: (event: React.MouseEvent<HTMLElement>) => void;
  coverPhotoBackgroundColor: string;
  addButtonText: string;
  addButtonVariant: AddButtonVariant;
}

const MerchCard = ({
  name,
  linkUrl,
  pointsCost,
  cashCost,
  image,
  isNew,
  isLowStock,
  hasStock,
  onAddButtonClick,
  isCollected,
  unlockLevel,
  redeemWithPoints,
  onMerchCardClick,
  coverPhotoBackgroundColor,
  addButtonText,
  addButtonVariant,
}: MerchCardProps) => {
  const { t } = useTranslation([I18N_NAMESPACES.COMMON, I18N_NAMESPACES.REWARDS]);
  const isUserLoggedIn = isLoggedIn();

  const isLocked = !!unlockLevel;
  const { isSmall } = useDeviceSize();

  const renderTag = (): ReactElement | null => {
    if (isCollected) {
      return (
        <Tag isDesktop={!isSmall} backgroundColor={colors.warning}>
          <CheckIcon />
          <TagText>{t("common:collected")}</TagText>
        </Tag>
      );
    }

    if (unlockLevel) {
      return (
        <Tag isDesktop={!isSmall} backgroundColor={colors.white}>
          <TagText>{t("rewards:unlocksAtLvl", { level: unlockLevel })}</TagText>
        </Tag>
      );
    }

    return null;
  };

  const renderImage = (): ReactElement => (
    <MerchCardImageHolder
      to={linkUrl}
      onClick={(event: React.MouseEvent<HTMLElement>) => onMerchCardClick(event)}
      backgroundColor={coverPhotoBackgroundColor}
      isLocked={isLocked}
    >
      <MerchCardImg
        // TODO: Add placeholder image.
        src={image ?? ""}
        alt={name ?? ""}
      />
    </MerchCardImageHolder>
  );

  const renderInfoLabel = (): ReactElement | null => {
    if (unlockLevel) {
      return (
        <InfoWrapper>
          <RewardLockedIcon />
        </InfoWrapper>
      );
    }

    let labelColor: string = "";
    let labelString: string = "";
    if (!hasStock) {
      labelColor = colors.gray;
      labelString = t("common:noStock");
    } else if (isLowStock) {
      labelColor = colors.danger;
      labelString = t("common:lowStock");
    } else if (isNew) {
      // isLowStock takes precedence over isNew
      labelColor = colors.success;
      labelString = t("common:new");
    }

    if (!labelColor && !labelString) {
      return null;
    }

    return (
      <InfoWrapper>
        <InfoLabel color={labelColor}>{labelString}</InfoLabel>
      </InfoWrapper>
    );
  };

  const pointsString = isSmall
    ? t("common:ptsAmount", { points: pointsCost })
    : t("common:pointsAmount", { points: pointsCost });
  const cashString = getCashAmountLabel(cashCost);

  const renderPrice = (): ReactElement => (
    <PriceWrapper>
      <PriceLabel>{redeemWithPoints ? pointsString : cashString}</PriceLabel>
    </PriceWrapper>
  );

  const renderAddButton = (): ReactElement => (
    <AddButton variant={addButtonVariant} onClick={(event) => onAddButtonClick(event)}>
      {addButtonText}
    </AddButton>
  );

  const renderFooter = (): ReactElement => (
    <MerchCardFooter>
      <MerchTitle to={linkUrl} onClick={(event: React.MouseEvent<HTMLElement>) => onMerchCardClick(event)}>
        {name}
      </MerchTitle>
      <MerchCardFooterBottomRow>
        {renderInfoLabel()}
        {renderPrice()}
        {isUserLoggedIn && renderAddButton()}
      </MerchCardFooterBottomRow>
    </MerchCardFooter>
  );

  return (
    <MerchCardWrapper>
      {renderImage()}
      {renderTag()}
      {renderFooter()}
    </MerchCardWrapper>
  );
};

export default MerchCard;
